import { useCachedData } from '../hooks/useCachedData';
import { endpoints, joinUrl, deleteApiCall } from '../api/config';

import { DataGridPro } from '@mui/x-data-grid-pro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Button, TextField, DialogActions, CircularProgress, Typography, InputAdornment, Box, Alert, Snackbar, Stack, Tooltip, FormControlLabel, Checkbox, Chip, FormControl, InputLabel, Select, MenuItem, OutlinedInput, ListItemText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '../api/config';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';
import { NEAR_FULLSCREEN_DIALOG_PAPER_SX } from '../constants/styles';

const MASTER_ADMIN_ENDPOINT = '/master-admin-details';
const ADMIN_REGISTER_ENDPOINT = '/admin/register';

const Admins = () => {
  const { logout } = useAuth();
  const { data, isLoading, error, refetch } = useCachedData(endpoints.admins, {
    refetchInterval: 30000, // 30 seconds
    staleTime: 20000, // 20 seconds
  });

  // Fetch roles for the Roles dropdown (web-only)
  const { data: rolesData } = useCachedData(endpoints.roles);

  // Derive web-platform role names from the /roles response
  const webRoleOptions = useMemo(() => {
    if (!rolesData) return [];
    // New API shape: { web: [...], mobile: [...] }
    if (Array.isArray(rolesData.web)) {
      return rolesData.web.map((r) => r.role).filter(Boolean);
    }
    // Legacy flat shape: filter by platform === 'web' or no platform
    let roles = [];
    if (Array.isArray(rolesData.roles)) roles = rolesData.roles;
    else if (Array.isArray(rolesData)) roles = rolesData;
    return roles
      .filter((r) => !r.platform || r.platform === 'web')
      .map((r) => r.role)
      .filter(Boolean);
  }, [rolesData]);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('admins-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('admins-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Default-hide the Filters column when data first loads
  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      setColumnVisibility((prev) => {
        if (typeof prev.filters === 'undefined') {
          return { ...prev, filters: false };
        }
        return prev;
      });
    }
  }, [data]);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletingUsername, setDeletingUsername] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    username: '',
    company_name: '',
    company_email: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    is_super_admin: false,
    allocated_roles: [],
    features: {
      locations: false,
      bins: false,
      inventory: false,
      items: false,
      salesorder: false,
      dropshipSalesOrders: false,
      salesOrderList: false,
      inbound: false,
      bincount: false,
      binAdjustment: false,
      binTransfer: false,
      homedepotProductsErrors: false,
      shippingLabelErrors: false,
      masterScraperData: false,
      fedexFileUpload: false,
      marketPlacePrice: false,
      itemSalesByCus: false,
      ltlInsufficientInventory: false,
      ltlPickProgress: false,
      regularDashboard: false,
      amAmxlDashboard: false,
      regularPickProgress: false
    }
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Add state for edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    company_name: '',
    company_email: '',
    phone_number: '',
    password: '',
    confirm_password: '',
    allocated_roles: [],
    features: {
      locations: false,
      bins: false,
      inventory: false,
      items: false,
      salesorder: false,
      dropshipSalesOrders: false,
      salesOrderList: false,
      inbound: false,
      bincount: false,
      binAdjustment: false,
      binTransfer: false,
      netsuiteErrors: false,
      ltlDashboard: false,
      ltlPickProgress: false,
      regularPickProgress: false,
      homedepotProductsErrors: false,
      shippingLabelErrors: false,
      masterScraperData: false,
      fedexFileUpload: false,
      marketPlacePrice: false,
      itemSalesByCus: false,
      ltlInsufficientInventory: false,
      regularDashboard: false,
      amAmxlDashboard: false,
      amAtsDashboard: false,
      amUpsDashboard: false,
      userPickStatus: false,
      PartsDashboard: false
    }
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Add state for edit password visibility
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);

  // Add refresh loading state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // State for Reset 2FA
  const [reset2FADialogOpen, setReset2FADialogOpen] = useState(false);
  const [adminToReset2FA, setAdminToReset2FA] = useState(null);
  const [reset2FALoading, setReset2FALoading] = useState(false);

  // Check if current user is super admin
  const isSuperAdmin = sessionStorage.getItem('isSuperAdmin') === 'true';

  const handleDeleteClick = (admin) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAdminToDelete(null);
  };

  const handleReset2FAClick = (admin) => {
    setAdminToReset2FA(admin);
    setReset2FADialogOpen(true);
  };

  const handleReset2FACancel = () => {
    setReset2FADialogOpen(false);
    setAdminToReset2FA(null);
  };

  const handleReset2FAConfirm = async () => {
    if (!adminToReset2FA) return;

    setReset2FALoading(true);
    try {
      const response = await api.post(`/2fa/reset/${adminToReset2FA.username}`);

      setSnackbar({
        open: true,
        message: `2FA reset successful for ${adminToReset2FA.username}. They will need to set it up on next login.`,
        severity: 'success'
      });

      setReset2FADialogOpen(false);
      setAdminToReset2FA(null);

      // Optionally refetch admin data
      refetch();
    } catch (err) {
      console.error('Reset 2FA error:', err);

      let errorMessage = 'Failed to reset 2FA';
      if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to reset 2FA';
      } else if (err.response?.status === 404) {
        errorMessage = 'User not found';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setReset2FALoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!adminToDelete) return;

    setDeleteLoading(true);
    setDeletingUsername(adminToDelete.username);
    setDeleteDialogOpen(false);

    // Check if the admin being deleted is the currently logged-in admin
    const currentUsername = sessionStorage.getItem('username');
    const isSelfDeletion = adminToDelete.username === currentUsername;

    try {
      await deleteApiCall(`/admin/delete/${adminToDelete.username}`);

      setSnackbar({
        open: true,
        message: isSelfDeletion ? 'Your admin account has been deleted. You will be redirected to the login page.' : 'Admin deleted successfully',
        severity: 'success'
      });
      
      if (isSelfDeletion) {
        // Clear session and redirect to login page after a short delay
        setTimeout(() => {
          sessionStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        // Refresh the data for other admin deletions
        refetch();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete admin',
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
      setDeletingUsername(null);
      setAdminToDelete(null);
    }
  };

  const handleEditClick = (admin) => {
    const adminFeatures = admin.features && typeof admin.features === 'object' ? admin.features : {};
    setEditForm({
      username: admin.username,
      company_name: admin.company_name || '',
      company_email: admin.company_email || '',
      phone_number: admin.phone_number || '',
      password: '',
      confirm_password: '',
      allocated_roles: Array.isArray(admin.allocated_roles) ? admin.allocated_roles : [],
      features: {
        ...adminFeatures,
        userPickStatus: adminFeatures.userPickStatus === true || admin.userPickStatus === true || admin.user_pick_status === true,
        PartsDashboard: adminFeatures.PartsDashboard === true || admin.PartsDashboard === true || admin.parts_dashboard === true,
        shippingLabelErrors: adminFeatures.shippingLabelErrors === true || admin.shippingLabelErrors === true || admin.shipping_label_errors === true,
        amAmxlDashboard: adminFeatures.amAmxlDashboard === true || admin.amAmxlDashboard === true,
        amAtsDashboard: adminFeatures.amAtsDashboard === true || admin.amAtsDashboard === true
      }
    });
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditForm({
      username: '',
      company_name: '',
      company_email: '',
      phone_number: '',
      password: '',
      confirm_password: '',
      allocated_roles: [],
      features: {
        locations: false,
        bins: false,
        inventory: false,
        items: false,
        salesorder: false,
        dropshipSalesOrders: false,
        salesOrderList: false,
        inbound: false,
        bincount: false,
        binAdjustment: false,
        binTransfer: false,
        homedepotProductsErrors: false,
        shippingLabelErrors: false,
        ltlDashboard: false,
        ltlPickProgress: false,
        regularPickProgress: false,
        fedexFileUpload: false,
        marketPlacePrice: false,
        itemSalesByCus: false,
        fedexShippingAnalysis: false,
        ltlInsufficientInventory: false,
        regularDashboard: false,
        amAmxlDashboard: false,
        amAtsDashboard: false,
        amUpsDashboard: false,
        userPickStatus: false,
        PartsDashboard: false
      }
    });
    setEditError('');
    setShowEditPassword(false);
    setShowEditConfirmPassword(false);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name.startsWith('feature_')) {
      // Handle feature checkboxes
      const featureName = name.replace('feature_', '');
      setEditForm(prev => ({
        ...prev,
        features: {
          ...(prev.features || {}),
          [featureName]: checked
        }
      }));
    } else {
      // Allow spaces only in company_name field
      if (name === 'company_name') {
        setEditForm(prev => ({ ...prev, [name]: value }));
      } else if (name === 'username') {
        // Only allow alphanumeric, underscore, and dollar sign in username
        const sanitizedValue = value.replace(/[^a-zA-Z0-9_$]/g, '');
        setEditForm(prev => ({ ...prev, [name]: sanitizedValue }));
      } else {
        // Remove spaces from all other fields
        setEditForm(prev => ({ ...prev, [name]: value.replace(/\s+/g, '') }));
      }
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError('');

    try {
      // Validate company name
      if (editForm.company_name.trim().length < 2) {
        setEditError('Company name must be at least 2 characters long');
        setEditLoading(false);
        return;
      }
      if (editForm.company_name.trim().length > 50) {
        setEditError('Company name cannot exceed 50 characters');
        setEditLoading(false);
        return;
      }

      // Validate company email
      if (editForm.company_email.includes(' ')) {
        setEditError('Email cannot contain spaces');
        setEditLoading(false);
        return;
      }
      if (editForm.company_email && (!editForm.company_email.includes('@') || !editForm.company_email.includes('.'))) {
        setEditError('Please enter a valid email address');
        setEditLoading(false);
        return;
      }

      // Check if email already exists (excluding current admin)
      if (data && Array.isArray(data) && editForm.company_email) {
        const emailExists = data.some(
          admin =>
            admin.username !== editForm.username && // Exclude current admin
            typeof admin.company_email === 'string' &&
            admin.company_email.toLowerCase() === editForm.company_email.toLowerCase()
        );
        if (emailExists) {
          setEditError('This email is already registered as an admin');
          setEditLoading(false);
          return;
        }
      }

      // Validate phone number
      if (editForm.phone_number && editForm.phone_number.includes(' ')) {
        setEditError('Phone number cannot contain spaces');
        setEditLoading(false);
        return;
      }
      if (editForm.phone_number && !/^\d+$/.test(editForm.phone_number)) {
        setEditError('Phone number can only contain digits');
        setEditLoading(false);
        return;
      }
      if (editForm.phone_number && editForm.phone_number.length < 10) {
        setEditError('Phone number must be at least 10 digits');
        setEditLoading(false);
        return;
      }
      if (editForm.phone_number && editForm.phone_number.length > 12) {
        setEditError('Phone number cannot exceed 12 digits');
        setEditLoading(false);
        return;
      }

      // Validate passwords if being updated
      if (editForm.password || editForm.confirm_password) {
        if (editForm.password !== editForm.confirm_password) {
          setEditError('Passwords do not match');
          setEditLoading(false);
          return;
        }
        if (editForm.password.includes(' ') || editForm.confirm_password.includes(' ')) {
          setEditError('Passwords cannot contain spaces');
          setEditLoading(false);
          return;
        }
        if (editForm.password.length < 4) {
          setEditError('Password must be at least 4 characters long');
          setEditLoading(false);
          return;
        }
      }

             // Prepare the payload
       const payload = {
         username: editForm.username,
         company_name: editForm.company_name.trim(),
         company_email: editForm.company_email,
         phone_number: editForm.phone_number,
         allocated_roles: editForm.allocated_roles || [],
         features: editForm.features || {
           locations: false,
           bins: false,
           inventory: false,
           items: false,
           salesorder: false,
           dropshipSalesOrders: false,
           salesOrderList: false,
           inbound: false,
           bincount: false,
           binAdjustment: false,
           binTransfer: false,
            homedepotProductsErrors: false,
          shippingLabelErrors: false,
            ltlDashboard: false,
            ltlPickProgress: false,
            regularPickProgress: false,
            fedexShippingAnalysis: false,
            marketPlacePrice: false,
            itemSalesByCus: false,
            ltlInsufficientInventory: false,
            regularDashboard: false,
            amAmxlDashboard: false,
            amAtsDashboard: false,
            userPickStatus: false,
            PartsDashboard: false
         },
         ...(editForm.password && { 
           password: editForm.password,
           confirm_password: editForm.confirm_password 
         })
       };

      // Make the PUT request to update admin profile
      await api.put(`/admin/profile/${editForm.username}`, payload);

      if (editForm.allocated_roles) {
        await api.post(endpoints.assignRoleToUser, {
          userid: editForm.username,
          roles: editForm.allocated_roles,
          platform: 'web'
        });
      }

      setSnackbar({
        open: true,
        message: 'Admin profile updated successfully',
        severity: 'success'
      });

      // Close the dialog and refresh the data
      handleEditClose();
      refetch();
    } catch (error) {
      console.error('Error updating admin profile:', error.response?.data);
      setEditError(error.response?.data?.detail || error.response?.data?.message || 'Failed to update admin profile');
    } finally {
      setEditLoading(false);
    }
  };

  const ADMIN_COL_EDIT = '__adm_edit';
  const ADMIN_COL_RESET_2FA = '__adm_reset_2fa';
  const ADMIN_COL_DELETE = '__adm_delete';

  // Generate columns with fixed order: username, company name, company email, per-action columns, created date
  const columns = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    const baseColumns = [];

    // 1. Username column (first column)
    baseColumns.push({
      accessorKey: 'username',
      header: 'Username',
      minSize: 200,
      size: 220,
      enableGlobalFilter: true,
      filterFn: 'customFilter',
      cell: info => {
        const value = info.getValue();
        return value || '-';
      }
    });

    // 2. Company Name column
    baseColumns.push({
      accessorKey: 'company_name',
      header: 'Company Name',
      minSize: 200,
      size: 220,
      enableGlobalFilter: true,
      filterFn: 'customFilter',
      cell: info => {
        const value = info.getValue();
        return value || '-';
      }
    });

    // 3. Company Email column
    baseColumns.push({
      accessorKey: 'company_email',
      header: 'Company Email',
      minSize: 200,
      size: 220,
      enableGlobalFilter: true,
      filterFn: 'customFilter',
      cell: info => {
        const value = info.getValue();
        return value || '-';
      }
    });

    const editButtonSx = {
      width: '100%',
      maxWidth: 140,
      textTransform: 'none',
      fontWeight: 500,
      boxShadow: 'none',
      whiteSpace: 'nowrap',
      justifyContent: 'flex-start',
      '&:hover': { boxShadow: 'none' }
      };

    // 4a. Edit (own column â€” avoids overlap when grid is narrow)
    baseColumns.push({
      accessorKey: ADMIN_COL_EDIT,
      header: 'Edit',
      enableSorting: false,
      minSize: 96,
      size: 108,
      cell: (info) => {
        const row = info.row?.original;
        if (!row?.username) return '-';
        return (
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
            sx={editButtonSx}
            startIcon={<EditIcon fontSize="small" />}
          >
            Edit
          </Button>
        );
      }
      });

        // 4b. Reset 2FA â€” column only when current user is super admin
    if (isSuperAdmin) {
      baseColumns.push({
        accessorKey: ADMIN_COL_RESET_2FA,
        header: 'Reset 2FA',
        enableSorting: false,
        minSize: 112,
        size: 128,
        cell: (info) => {
          const row = info.row?.original;
          if (!row?.username) return '-';
          return (
            <Tooltip title="Reset Two-Factor Authentication">
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReset2FAClick(row);
                }}
                sx={{
                  width: '100%',
                  maxWidth: 140,
                  textTransform: 'none',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  justifyContent: 'flex-start'
      }}
              >
                Reset 2FA
              </Button>
            </Tooltip>
          );
        }
      });
    }

    // 4c. Delete
    baseColumns.push({
      accessorKey: ADMIN_COL_DELETE,
      header: 'Delete',
      enableSorting: false,
      minSize: 96,
      size: 108,
      cell: (info) => {
        const row = info.row?.original;
        if (!row?.username) return '-';
        const isDeleting = deleteLoading && deletingUsername === row.username;
        return (
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            disabled={deleteLoading}
            sx={{
              ...editButtonSx,
              justifyContent: 'center',
              maxWidth: 140
      }}
          >
            {isDeleting ? (
              <CircularProgress size={16} color="inherit" aria-label="Deleting admin" />
            ) : (
              'Delete'
            )}
          </Button>
        );
      }
      });

    // 7. Created Date column (if exists)
    if (data[0].created_at) {
      baseColumns.push({
        accessorKey: 'created_at',
        header: 'Created Date',
        minSize: 200,
        size: 220,
        enableGlobalFilter: true,
        filterFn: 'customFilter',
        cell: info => {
          const value = info.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date)) {
                return date.toLocaleString();
              }
            } catch (e) {
              return String(value);
            }
          }
          return value || '-';
        }
      });
    }

    // Add any remaining columns that weren't in the fixed order
    const keys = Object.keys(data[0]).filter(key => {
      const lowerKey = key.toLowerCase();
      return !lowerKey.includes('password') && 
             !['username', 'company_name', 'company_email', 'created_at', 'features', ADMIN_COL_EDIT, ADMIN_COL_RESET_2FA, ADMIN_COL_DELETE].includes(key) &&
             !lowerKey.includes('userpickstatus') &&
             lowerKey !== 'user_pick_status' &&
             !lowerKey.includes('partsdashboard') &&
             lowerKey !== 'parts_dashboard';
    });
    
    keys.forEach(key => {
      const columnExists = baseColumns.some(col => col.accessorKey === key);
      if (!columnExists) {
        baseColumns.push({
          accessorKey: key,
          header: key === 'features' ? 'Features' : (key === 'filters' ? 'Filters' : key)
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' '),
          minSize: key === 'features' ? 600 : (key === 'filters' ? 320 : 200),
          size: key === 'features' ? 700 : (key === 'filters' ? 380 : 220),
          enableGlobalFilter: key !== 'features', // Disable global filter for features as it's an object
          filterFn: 'customFilter',
          cell: info => {
            const value = info.getValue();
            if (key === 'netsuite_response' && value && typeof value === 'object') {
              return (
                <Button
                  variant="contained"
                  size="small"
                  sx={{ minWidth: 100 }}
                  onClick={() => {
                    // Implement the logic to handle viewing netsuite_response
                  }}
                >
                  View Response
                </Button>
              );
            }
            // Special handling for features object
            if (key === 'features' && value && typeof value === 'object') {
              const featureEntries = Object.entries(value);
              const enabledFeatures = featureEntries.filter(([_, enabled]) => enabled).map(([feature]) => feature);
              const disabledFeatures = featureEntries.filter(([_, enabled]) => !enabled).map(([feature]) => feature);
              
              return (
                <Tooltip 
                  title={`Features configuration for ${info.row.original.username || 'this admin'}`}
                  placement="top"
                  arrow
                >
                  <Box sx={{ p: 0.15, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1, maxWidth: '100%', maxHeight: '32px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.1, minWidth: 0, lineHeight: 1.1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main', minWidth: '38px', fontSize: '0.55rem', flexShrink: 0, lineHeight: 1.1 }}>
                        Enabled:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.55rem', wordBreak: 'break-word', whiteSpace: 'normal', flex: 1, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                        {enabledFeatures.length > 0 ? 
                          enabledFeatures.map(feature => 
                            feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
                          ).join(', ')
                          : 'None'
                        }
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, lineHeight: 1.1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary', minWidth: '38px', fontSize: '0.55rem', flexShrink: 0, lineHeight: 1.1 }}>
                        Disabled:
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.55rem', wordBreak: 'break-word', whiteSpace: 'normal', flex: 1, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                        {disabledFeatures.length > 0 ? 
                          disabledFeatures.map(feature => 
                            feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
                          ).join(', ')
                          : 'All enabled'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              );
            }
            // Special handling for filters array
            if (key === 'filters') {
              const arr = Array.isArray(value) ? value : [];
              if (arr.length === 0) return '-';
              return (
                <Tooltip title="Saved column filters" placement="top" arrow>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%' }}>
                    {arr.map((f, idx) => {
                      const label = f && typeof f === 'object' ? `${f.key || ''} = ${f.type || ''}` : String(f ?? '');
                      return (
                        <Chip key={idx} size="small" label={label} variant="outlined" sx={{ maxWidth: 220 }} />
                      );
                    })}
                  </Box>
                </Tooltip>
              );
            }
            return value || '-';
          }
        });
      }
    });

    return baseColumns;
  }, [data, deleteLoading, deletingUsername, isSuperAdmin]);

  const handleCreateOpen = () => {
    setForm({
      username: '',
      company_name: '',
      company_email: '',
      phone_number: '',
      password: '',
      confirm_password: '',
      is_super_admin: false,
      features: {
        locations: false,
        bins: false,
        inventory: false,
        items: false,
        salesorder: false,
        dropshipSalesOrders: false,
        salesOrderList: false,
        inbound: false,
        bincount: false,
        binAdjustment: false,
        binTransfer: false,
        homedepotProductsErrors: false,
        shippingLabelErrors: false,
        masterScraperData: false,
        ltlInsufficientInventory: false,
        ltlPickProgress: false,
        regularPickProgress: false,
        regularDashboard: false,
        amAmxlDashboard: false,
        amAtsDashboard: false,
        amUpsDashboard: false,
        userPickStatus: false,
        PartsDashboard: false
      }
    });
    setFormError('');
    setFormSuccess('');
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
    setForm({
      username: '',
      company_name: '',
      company_email: '',
      phone_number: '',
      password: '',
      confirm_password: '',
      is_super_admin: false,
        features: {
          locations: false,
          bins: false,
          inventory: false,
          items: false,
          salesorder: false,
          dropshipSalesOrders: false,
          salesOrderList: false,
          inbound: false,
          bincount: false,
          binAdjustment: false,
          binTransfer: false,
          homedepotProductsErrors: false,
          netsuiteErrors: false,
          shippingLabelErrors: false,
          ltlPickProgress: false,
          regularPickProgress: false,
          fedexFileUpload: false,
          marketPlacePrice: false,
          itemSalesByCus: false,
          fedexShippingAnalysis: false,
          ltlInsufficientInventory: false,
          userPickStatus: false,
          PartsDashboard: false
        }
    });
    setFormError('');
    setFormSuccess('');
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'is_super_admin') {
        setForm(prev => ({ ...prev, [name]: checked }));
      } else if (name.startsWith('feature_')) {
        // Handle feature checkboxes
        const featureName = name.replace('feature_', '');
        setForm(prev => ({
          ...prev,
          features: {
            ...(prev.features || {}),
            [featureName]: checked
          }
        }));
      }
    } else {
      // Allow spaces only in company_name field
      if (name === 'company_name') {
        setForm(prev => ({ ...prev, [name]: value }));
      } else if (name === 'username') {
        // Only allow alphanumeric, underscore, and dollar sign in username
        const sanitizedValue = value.replace(/[^a-zA-Z0-9_$]/g, '');
        setForm(prev => ({ ...prev, [name]: sanitizedValue }));
      } else {
        // Remove spaces from all other fields
        setForm(prev => ({ ...prev, [name]: value.replace(/\s+/g, '') }));
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setFormSuccess('');

    // Validate username
    if (form.username.includes(' ')) {
      setFormError('Username cannot contain spaces');
      setFormLoading(false);
      return;
    }

    // Check if username contains at least one alphanumeric character
    if (!/[a-zA-Z0-9]/.test(form.username)) {
      setFormError('Username must contain at least one letter or number');
      setFormLoading(false);
      return;
    }

    // Check if username contains only valid characters (alphanumeric, underscore, and dollar sign)
    if (!/^[a-zA-Z0-9_$]+$/.test(form.username)) {
      setFormError('Username can only contain letters, numbers, underscore (_) and dollar sign ($)');
      setFormLoading(false);
      return;
    }

    // Validate username length
    if (form.username.length < 3) {
      setFormError('Username must be at least 3 characters long');
      setFormLoading(false);
      return;
    }
    if (form.username.length > 20) {
      setFormError('Username cannot exceed 20 characters');
      setFormLoading(false);
      return;
    }

    // Validate company name
    if (form.company_name.trim().length < 2) {
      setFormError('Company name must be at least 2 characters long');
      setFormLoading(false);
      return;
    }
    if (form.company_name.trim().length > 50) { // Increased max length for company name
      setFormError('Company name cannot exceed 50 characters');
      setFormLoading(false);
      return;
    }

    // Validate company email (no spaces allowed)
    if (form.company_email.includes(' ')) {
      setFormError('Email cannot contain spaces');
      setFormLoading(false);
      return;
    }
    if (!form.company_email.includes('@') || !form.company_email.includes('.')) {
      setFormError('Please enter a valid email address');
      setFormLoading(false);
      return;
    }

    // Check if email already exists in the current admins data
    if (data && Array.isArray(data)) {
      const emailExists = data.some(
        admin =>
          typeof admin.company_email === 'string' &&
          admin.company_email.toLowerCase() === form.company_email.toLowerCase()
      );
      if (emailExists) {
        setFormError('This email is already registered as an admin');
        setFormLoading(false);
        return;
      }
    }

    // Validate phone number (no spaces allowed)
    if (form.phone_number && form.phone_number.includes(' ')) {
      setFormError('Phone number cannot contain spaces');
      setFormLoading(false);
      return;
    }
    // Only allow digits in phone number
    if (form.phone_number && !/^\d+$/.test(form.phone_number)) {
      setFormError('Phone number can only contain digits');
      setFormLoading(false);
      return;
    }
    // Check minimum length
    if (form.phone_number && form.phone_number.length < 10) {
      setFormError('Phone number must be at least 10 digits');
      setFormLoading(false);
      return;
    }
    // Check maximum length
    if (form.phone_number && form.phone_number.length > 12) {
      setFormError('Phone number cannot exceed 12 digits');
      setFormLoading(false);
      return;
    }

    // Validate passwords match and no spaces
    if (form.password !== form.confirm_password) {
      setFormError('Passwords do not match');
      setFormLoading(false);
      return;
    }
    if (form.password.includes(' ') || form.confirm_password.includes(' ')) {
      setFormError('Passwords cannot contain spaces');
      setFormLoading(false);
      return;
    }
    if (form.password.length < 4) {
      setFormError('Password must be at least 4 characters long');
      setFormLoading(false);
      return;
    }

    try {
      // Only allow super admins to create other super admins
      const isCurrentUserSuperAdmin = sessionStorage.getItem('isSuperAdmin') === 'true';
      const canCreateSuperAdmin = isCurrentUserSuperAdmin && form.is_super_admin;
      
             const payload = {
         username: form.username,
         company_name: form.company_name.trim(), // Trim any leading/trailing spaces
         company_email: form.company_email,
         phone_number: form.phone_number,
         password: form.password,
         confirm_password: form.confirm_password,
         is_super_admin: canCreateSuperAdmin, // Only true if current user is super admin AND checkbox is checked
         allocated_roles: form.allocated_roles || [],
         features: form.features || {
           locations: false,
           bins: false,
           inventory: false,
           items: false,
           salesorder: false,
           inbound: false,
           bincount: false,
           binAdjustment: false,
           binTransfer: false,
            homedepotProductsErrors: false,
          shippingLabelErrors: false,
            fedexFileUpload: false,
            marketPlacePrice: false,
            itemSalesByCus: false,
            fedexShippingAnalysis: false,
            ltlInsufficientInventory: false,
            regularDashboard: false,
            amAmxlDashboard: false,
            userPickStatus: false,
            PartsDashboard: false
         }
       };
      await api.post(ADMIN_REGISTER_ENDPOINT, payload);

      if (form.allocated_roles?.length) {
        await api.post(endpoints.assignRoleToUser, {
          userid: form.username,
          roles: form.allocated_roles,
          platform: 'web'
        });
      }

      setFormSuccess('Admin created successfully!');
       setForm({
         username: '',
         company_name: '',
         company_email: '',
         phone_number: '',
         password: '',
         confirm_password: '',
         is_super_admin: false,
         features: {
           locations: false,
           bins: false,
           inventory: false,
           items: false,
           salesorder: false,
           dropshipSalesOrders: false,
           salesOrderList: false,
           inbound: false,
           bincount: false,
           binAdjustment: false,
           binTransfer: false,
            homedepotProductsErrors: false,
            netsuiteErrors: false,
            shippingLabelErrors: false,
            ltlPickProgress: false,
            regularPickProgress: false,
           masterScraperData: false,
           fedexFileUpload: false,
           marketPlacePrice: false,
           itemSalesByCus: false,
           fedexShippingAnalysis: false,
           ltlInsufficientInventory: false,
           regularDashboard: false,
          amAmxlDashboard: false,
           userPickStatus: false,
           PartsDashboard: false
         }
       });
      setTimeout(() => {
        setCreateOpen(false);
        refetch();
      }, 1000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || '';
      setFormError(errorMessage.includes('duplicate key value') || errorMessage.includes('already exists') 
        ? 'Admin already exists' 
        : 'Failed to create admin');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const tableData = useMemo(() => {
    if (!data) return [];
    return data.map((row) => ({
      ...row,
      [ADMIN_COL_EDIT]: true,
      ...(isSuperAdmin ? { [ADMIN_COL_RESET_2FA]: true } : {}),
      [ADMIN_COL_DELETE]: true
      }));
  }, [data, isSuperAdmin]);

  // Handle refresh click
  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to refresh data',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const adminGrid = useDataGridProTable({
    data: tableData,
    loading: isLoading,
    error: error ? error.message || 'Failed to load admins' : null,
    title: 'Admins',
    columns,
    onRowClick: (row) => handleEditClick(row),
    onDataRefresh: refetch,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    searchOnEnter: false,
    muiTableContainerProps: { sx: {} },
    actionButtons: [
      <Stack key="admin-actions" direction="row" spacing={1}>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
      }
      }}
          >
            {isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          onClick={() => {
            setCreateOpen(true);
            setForm({
              username: '',
              company_name: '',
              company_email: '',
              phone_number: '',
              password: '',
              confirm_password: '',
              is_super_admin: false,
              features: {
                locations: false,
                bins: false,
                inventory: false,
                items: false,
                salesorder: false,
                dropshipSalesOrders: false,
                salesOrderList: false,
                inbound: false,
                bincount: false,
                binAdjustment: false,
                binTransfer: false,
                homedepotProducts: false,
                homedepotProductsErrors: false,
                shippingLabelErrors: false,
                ltlPickProgress: false,
                regularPickProgress: false,
                regularDashboard: false
      }
      });
            setFormError('');
            setFormSuccess('');
          }}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
      }
      }}
        >
          Create Admin
        </Button>
      </Stack>,
    ]
      });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <MuiXDataGridSurface hook={adminGrid} DataGridPro={DataGridPro} />
      </Box>

      <Dialog open={createOpen} onClose={handleCreateClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          Create Admin
          <IconButton
            aria-label="Close create admin dialog"
            onClick={handleCreateClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent dividers>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            {formSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {formSuccess}
              </Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              value={form.username}
              onChange={handleFormChange}
              autoComplete="username"
              error={!!formError && formError.toLowerCase().includes('username')}
              disabled={formLoading}
              inputProps={{ 
                maxLength: 20,
                pattern: "[a-zA-Z0-9_$]+",
                title: "Username can only contain letters, numbers, underscore (_) and dollar sign ($)"
              }}
              helperText="Must contain at least one letter or number. Only letters, numbers, underscore (_) and dollar sign ($) are allowed."
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="company_name"
              label="Company Name"
              name="company_name"
              value={form.company_name}
              onChange={handleFormChange}
              autoComplete="organization"
              error={!!formError && formError.toLowerCase().includes('company_name')}
              disabled={formLoading}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="company_email"
              label="Company Email"
              name="company_email"
              type="email"
              value={form.company_email}
              onChange={handleFormChange}
              autoComplete="email"
              error={!!formError && formError.toLowerCase().includes('email')}
              disabled={formLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="phone_number"
              label="Phone Number"
              name="phone_number"
              value={form.phone_number}
              onChange={handleFormChange}
              autoComplete="tel"
              error={!!formError && formError.toLowerCase().includes('phone_number')}
              disabled={formLoading}
              inputProps={{ 
                maxLength: 12,
                minLength: 10,
                pattern: "[0-9]{10,12}",
                title: "Phone number must be between 10 and 12 digits"
              }}
              helperText="Must be between 10 and 12 digits"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="password"
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleFormChange}
              autoComplete="new-password"
              error={!!formError && formError.toLowerCase().includes('password')}
              disabled={formLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
      }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="confirm_password"
              label="Confirm Password"
              name="confirm_password"
              type={showPassword ? 'text' : 'password'}
              value={form.confirm_password}
              onChange={handleFormChange}
              autoComplete="new-password"
              error={!!formError && formError.toLowerCase().includes('confirm_password') || (form.password !== form.confirm_password && form.confirm_password.length > 0)}
              disabled={formLoading}
            />
            {/* Only super admins can see and use this checkbox to create other super admins */}
            {sessionStorage.getItem('isSuperAdmin') === 'true' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_super_admin}
                    onChange={handleFormChange}
                    name="is_super_admin"
                  />
                }
                label="Add as a Super Admin"
              />
            )}

            {/* Roles Section */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Roles
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="create-admin-roles-label">Roles (Web)</InputLabel>
              <Select
                labelId="create-admin-roles-label"
                id="create-admin-roles"
                multiple
                value={form.allocated_roles || []}
                onChange={(e) => {
                  const { value } = e.target;
                  setForm((prev) => ({
                    ...prev,
                    allocated_roles: typeof value === 'string' ? value.split(',') : value
                  }));
                }}
                input={<OutlinedInput label="Roles (Web)" />}
                renderValue={(selected) => selected.join(', ')}
                disabled={formLoading}
              >
                {webRoleOptions.length === 0 ? (
                  <MenuItem disabled>No web roles available</MenuItem>
                ) : (
                  webRoleOptions.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Checkbox checked={(form.allocated_roles || []).indexOf(role) > -1} />
                      <ListItemText primary={role} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

          </DialogContent>

          <DialogActions>
            <Button onClick={handleCreateClose} disabled={formLoading}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" disabled={formLoading}>
              {formLoading ? <CircularProgress size={20} aria-label="Creating admin" /> : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: '400px' },
            m: { xs: 2, sm: 3 }
          }
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {adminToDelete?.username === sessionStorage.getItem('username') ? (
            <>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, color: 'error.main' }}>
                âš ï¸ Self-Deletion Warning
              </Typography>
              <Typography variant="body2">
                You are about to delete your own admin account. This action will:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>Permanently remove your admin access</li>
                <li>Log you out immediately</li>
                <li>Redirect you to the login page</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, fontWeight: 500 }}>
                Are you sure you want to proceed with deleting your own admin account?
              </Typography>
            </>
          ) : (
            <>Are you sure you want to delete admin {adminToDelete?.username}?</>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} aria-label="Deleting admin" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset 2FA Confirmation Dialog */}
      <Dialog
        open={reset2FADialogOpen}
        onClose={handleReset2FACancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reset Two-Factor Authentication
          <IconButton
            onClick={handleReset2FACancel}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will disable 2FA for this user. They will be required to set it up again on their next login.
          </Alert>
          <Typography>
            Are you sure you want to reset 2FA for user{' '}
            <strong>{adminToReset2FA?.username}</strong>?
          </Typography>
          {adminToReset2FA?.company_email && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Email: {adminToReset2FA.company_email}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleReset2FACancel} disabled={reset2FALoading}>
            Cancel
          </Button>
          <Button
            onClick={handleReset2FAConfirm}
            variant="contained"
            color="warning"
            disabled={reset2FALoading}
          >
            {reset2FALoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Resetting...
              </>
            ) : (
              'Reset 2FA'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '680px',
            maxHeight: '90vh',
            m: { xs: 1.5, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{ px: 3, py: 2 }}>
          Edit Admin
          <IconButton
            aria-label="close"
            onClick={handleEditClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, overflowY: 'auto' }}>
          <Box component="form" onSubmit={handleEditSubmit} sx={{ mt: 0.5, maxWidth: '100%' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5
              }}
            >
            <TextField
              required
              fullWidth
              label="Username"
              name="username"
              value={editForm.username}
              disabled
              sx={{ mb: 0 }}
            />
            <TextField
              required
              fullWidth
              name="company_name"
              label="Company Name"
              value={editForm.company_name}
              onChange={handleEditFormChange}
              disabled={editLoading}
            />
            <TextField
              required
              fullWidth
              name="company_email"
              label="Company Email"
              type="email"
              value={editForm.company_email}
              onChange={handleEditFormChange}
              disabled={editLoading}
            />
            <TextField
              fullWidth
              name="phone_number"
              label="Phone Number"
              value={editForm.phone_number}
              onChange={handleEditFormChange}
              disabled={editLoading}
              helperText="Phone number is optional"
            />
            <TextField
              fullWidth
              name="password"
              label="New Password"
              type={showEditPassword ? 'text' : 'password'}
              value={editForm.password}
              onChange={handleEditFormChange}
              disabled={editLoading}
              helperText="Optional - leave blank to keep current password"
              InputLabelProps={{
                sx: {
                  fontSize: '0.85rem',
                  maxWidth: 'calc(100% - 48px)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      edge="end"
                      size="small"
                    >
                      {showEditPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              name="confirm_password"
              label="Confirm New Password"
              type={showEditConfirmPassword ? 'text' : 'password'}
              value={editForm.confirm_password}
              onChange={handleEditFormChange}
              disabled={editLoading}
              helperText="Enter new password above first"
              InputLabelProps={{
                sx: {
                  fontSize: '0.85rem',
                  maxWidth: 'calc(100% - 48px)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}
                      edge="end"
                      size="small"
                    >
                      {showEditConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            </Box>

            {/* Roles Section */}
            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Roles
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="edit-admin-roles-label">Roles (Web)</InputLabel>
              <Select
                labelId="edit-admin-roles-label"
                id="edit-admin-roles"
                multiple
                value={editForm.allocated_roles || []}
                onChange={(e) => {
                  const { value } = e.target;
                  setEditForm((prev) => ({
                    ...prev,
                    allocated_roles: typeof value === 'string' ? value.split(',') : value
                  }));
                }}
                input={<OutlinedInput label="Roles (Web)" />}
                renderValue={(selected) => selected.join(', ')}
                disabled={editLoading}
              >
                {webRoleOptions.length === 0 ? (
                  <MenuItem disabled>No web roles available</MenuItem>
                ) : (
                  webRoleOptions.map((role) => (
                    <MenuItem key={role} value={role}>
                      <Checkbox checked={(editForm.allocated_roles || []).indexOf(role) > -1} />
                      <ListItemText primary={role} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {editError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {editError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Button onClick={handleEditClose} disabled={editLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
            endIcon={editLoading ? <CircularProgress size={20} /> : null}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default Admins; 
